import React, { Fragment, useEffect, useState } from "react";
import ReactJWPlayer from "react-jw-player";
import {
  any,
  anyPass,
  ascend,
  clamp,
  equals,
  filter,
  findIndex,
  identity,
  map,
  pipe,
  prop,
  propEq,
  sort,
} from "ramda";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { HIDE_VIEWER } from "media-viewer/mediaViewerRedux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRotateLeft,
  faArrowRotateRight,
  faBackwardStep,
  faBars,
  faChevronDown,
  faCircleX,
  faForwardStep,
  faPause,
  faPlay,
  faWaveform,
  faWaveformLines,
} from "@fortawesome/pro-regular-svg-icons";
import {
  useCompoundChildren,
  useContentFiles,
} from "../creator-application/content/contentServices";
import { formatDuration, isAudiobook } from "../util/mediaUtil";
import { notBlank } from "../util/ext";

import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import { IconButton as MuiIconButton } from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Skeleton from "@material-ui/lab/Skeleton";
import Slide from "@material-ui/core/Slide";
import Typography from "@material-ui/core/Typography";
import { NODE_ENV } from "../props";

const playlistHeaderHeight = "50px";
const AUDIO_QUALITY_BTN_WIDTH = 150;

const useStyles = makeStyles(theme => ({
  buttonAudioQuality: {
    width: AUDIO_QUALITY_BTN_WIDTH,
  },

  buttonIcon: {
    borderStyle: "solid",
    borderWidth: 2,
    borderColor: theme.palette.primary.brandPrimary,
  },

  dialogRoot: {
    position: "relative",
  },

  dialogPaper: {
    overflowY: "hidden",
  },

  graphicEqIcon: {
    fontSize: "10rem",
  },

  icon: {
    color: theme.palette.primary.brandPrimary,
    fontSize: "2.4rem",
  },

  menu: {
    width: AUDIO_QUALITY_BTN_WIDTH,
    marginTop: 5,
  },

  playbackRate: {
    color: theme.palette.primary.brandPrimary,
    fontWeight: 700,
  },

  player: {
    "& .jw-skin-five": {
      "& .jw-background-color": {
        background: "white",
      },

      "& .jw-text": {
        color: theme.palette.primary.brandPrimary,
      },

      "& .jw-button-color": {
        color: theme.palette.primary.brandPrimary,
        fill: theme.palette.primary.brandPrimary,
      },

      "& .jw-progress": {
        color: theme.palette.primary.brandPrimary,
        background: theme.palette.primary.brandPrimary,
      },

      "& .jw-icon-playback, & .jw-icon-rewind, & .jw-icon-next": {
        display: "none !important",
      },
    },
  },

  playlist: {
    width: "100%",
    position: "absolute",
    zIndex: 2,
    bottom: 0,
    height: "70%",
  },

  playlistContainer: {
    overflowY: "auto",
    height: `calc(100% - ${playlistHeaderHeight})`,
    gap: 10,
  },

  playlistHeader: {
    height: playlistHeaderHeight,
    borderTopWidth: 1,
    borderTopColor: theme.palette.secondary.light,
    borderTopStyle: "solid",
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.secondary.light,
    borderBottomStyle: "solid",
  },

  thumbnail: {
    maxHeight: 325,
    maxWidth: 200,
  },

  track: {
    padding: theme.spacing(3),
    cursor: "pointer",
    "&:hover": {
      background: theme.palette.secondary.light,
    },
  },

  trackSelected: {
    fontWeight: 700,
    color: theme.palette.primary.brandPrimary,
  },

  trackSequence: {
    paddingRight: theme.spacing(6),
  },
}));

const IconButton = withStyles(theme => ({
  root: {
    height: 50,
    width: 50,
  },
}))(MuiIconButton);

const getSources = ({ type, formats, fileName }) => {
  const bitRateOrder = ["64k", "192k"];

  const getBitRateOrderValue = bitRate => {
    const index = bitRateOrder.indexOf(bitRate);
    return index === -1 ? bitRateOrder.length : index;
  };

  return pipe(
    map(format => ({
      file: equals(type, "AUDIOBOOK")
        ? format.url.replace(/content_/, `content/${fileName}_`)
        : format.url,
      label: format.bitRate === "64k" ? "Standard" : "High",
      default: format.bitRate === "192k",
      bitrate: format.bitRate,
    })),
    sources =>
      sources.sort(
        (a, b) => getBitRateOrderValue(a.label) - getBitRateOrderValue(b.label),
      ),
  )(formats);
};

const isAlbumTrack = media =>
  media.parentContent && media.parentContent.type === "ALBUM";
const isAlbum = media => equals(media.type, "ALBUM") || isAlbumTrack(media);

const AudioViewerDialog = () => {
  const classes = useStyles();
  const { media } = useSelector(state => state.mediaViewer);
  const dispatch = useDispatch();
  const onClose = () => dispatch({ type: HIDE_VIEWER });

  //-- Manage Player State
  const contentFiles = useContentFiles({ id: media.id });
  const compoundChildren = useCompoundChildren(
    isAlbumTrack(media) ? media.parentContent.id : media.id,
  );

  const [player, setPlayer] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playbackRate, setPlaybackRate] = useState("1x");
  const [quality, setQuality] = useState("192k");
  const [error, setError] = useState(null);
  useEffect(() => {
    // a little messy and redundant...
    if (isAlbum(media)) {
      if (compoundChildren.isSuccess) {
        const tracks = pipe(
          filter(propEq("mimeType", "audio/mpeg")),
          sort(ascend(prop("childOrder"))),
          map(track => ({
            ...track,
            sources: getSources({
              type: track.type,
              formats: track.audioFormats,
              fileName: null,
            }),
            mediaid: track.id,
          })),
        )(compoundChildren.data);

        setTracks(tracks);
        setCurrentTrack(
          isAlbumTrack(media) ? tracks[media.childOrder] : tracks[0],
        );
      }
    } else {
      if (contentFiles.isSuccess) {
        const tracks = pipe(
          filter(propEq("mimeType", "audio/mpeg")),
          sort(ascend(prop("sequence"))),
          map(file => ({
            ...file,
            sources: getSources({
              type: media.type,
              formats: media.audioFormats,
              fileName: isAudiobook(media) ? file.fileName : null,
            }),
            mediaid: file.id,
          })),
        )(contentFiles.data);

        setTracks(tracks);
        setCurrentTrack(tracks[0]);
      }
    }
  }, [
    media,
    compoundChildren.isSuccess,
    compoundChildren.data,
    contentFiles.isSuccess,
    contentFiles.data,
  ]);

  const handlePlayerReady = () => setPlayer(window.jwplayer("player"));
  const handleAutoStart = () => setPlaying(true);
  const handleOnPlay = () => setPlaying(true);
  const handleOnResume = () => setPlaying(true);
  const handleOnPause = () => setPlaying(false);
  const handleOnError = error => {
    console.log(error);
    setError(`Playback Error: ${error.code}:${error.message}`);
  };
  const handleOnPlayAttemptFailed = error => {
    console.log(error);
    setError(`Playback Error: ${error.code}:${error.error.message}`);
  };
  const handleOnAutoStartNotAllowed = error => {
    console.log(error);
    setError(`Autostart Not Allowed: ${error.reason}:${error.error}`);
  };
  const handleOnPlayerSetupError = errorMessage => {
    console.log(errorMessage);
    setError(`Player Setup Error: ${errorMessage}`);
  };

  const handleTrackChange = track => {
    if (player) {
      setCurrentTrack(track);
      const newTrackIndex = findIndex(propEq("id", track.id), tracks);
      player.playlistItem(newTrackIndex);
      setPlaying(true);
      setShowPlaylist(false);
    }
  };

  const handleNextTrack = () => {
    if (player) {
      const currentIndex = player.getPlaylistIndex();
      player.next();
      setPlaying(true);
      setCurrentTrack(tracks[currentIndex + 1]);
    }
  };

  const handlePrevTrack = e => {
    e.preventDefault();
    if (player) {
      const currentIndex = player.getPlaylistIndex();
      if (currentIndex > 0) {
        player.playlistItem(currentIndex - 1);
        setCurrentTrack(tracks[currentIndex - 1]);
      } else {
        player.seek(0);
        setCurrentTrack(tracks[0]);
      }
      setPlaying(true);
    }
  };

  const handlePlaybackClick = () => {
    if (player) {
      player.getState() === "playing" ? player.pause() : player.play();
    }
  };

  const seek = timeDiff => {
    if (player) {
      const position = clamp(
        0,
        player.getDuration(),
        player.getPosition() + timeDiff,
      );
      player.seek(position);
    }
  };

  const onClickSkipForward = e => {
    e.preventDefault();
    seek(30);
  };

  const onClickSkipBack = e => {
    e.preventDefault();
    seek(-30);
  };

  const onClickChangePlaybackSpeed = e => {
    e.preventDefault();
    if (player) {
      const currentRate = player.getPlaybackRate();
      const newRate =
        currentRate >= 1.5
          ? 0.5
          : currentRate >= 1 && currentRate <= 1.25
          ? currentRate + 0.25
          : currentRate + 0.5;
      player.setPlaybackRate(newRate);
      setPlaybackRate(`${newRate}x`);
    }
  };

  const handleClickAudioQuality = () => {
    if (player) {
      const qualities = player.getQualityLevels();
      const currentQualityIndex = player.getCurrentQuality();

      const isCurrentlyStandard =
        qualities[currentQualityIndex].bitrate === "64k";

      const indexToPlay = findIndex(
        propEq("bitrate", isCurrentlyStandard ? "192k" : "64k"),
        qualities,
      );

      player.setCurrentQuality(indexToPlay);
      setQuality(qualities[indexToPlay].bitrate);
    }
  };

  //-- Manage Playlist Drawer
  const [showPlaylist, setShowPlaylist] = useState(false);
  const handleOpenPlaylist = () => setShowPlaylist(true);
  const handleClosePlaylist = () => setShowPlaylist(false);

  //-- Manage Audio Quality Menu
  const [anchor, setAnchor] = useState(null);
  const handleClickMenu = event => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  };

  const handleCloseMenu = event => {
    event.stopPropagation();
    setAnchor(null);
  };

  return (
    <Dialog
      onClose={onClose}
      aria-label="audio player"
      open={true}
      classes={{
        root: classes.dialogRoot,
        paper: classes.dialogPaper,
      }}
    >
      <DialogTitle disableTypography aria-label="audio player">
        <Grid
          container
          justify={
            player && player.getQualityLevels().length > 1
              ? "space-between"
              : "flex-end"
          }
          alignItems="center"
        >
          {player && player.getQualityLevels().length > 1 && (
            <Grid item>
              <Button
                className={classes.buttonAudioQuality}
                variant="outlined"
                color="primary"
                onClick={handleClickMenu}
                startIcon={<FontAwesomeIcon icon={faWaveform} />}
                endIcon={<FontAwesomeIcon icon={faChevronDown} />}
              >
                {quality === "64k" ? "Standard" : "High"}
              </Button>
              <Menu
                id="audio-quality-select-menu"
                anchorEl={anchor}
                keepMounted
                open={Boolean(anchor)}
                onClose={handleCloseMenu}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                getContentAnchorEl={null}
                classes={{ paper: classes.menu }}
              >
                <MenuItem onClick={handleClickAudioQuality}>
                  {quality === "64k" ? "High" : "Standard"}
                </MenuItem>
              </Menu>
            </Grid>
          )}
          <IconButton onClick={onClose}>
            <FontAwesomeIcon icon={faCircleX} className={classes.icon} />
          </IconButton>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={6} alignItems="center">
          <Grid item>
            <Typography style={{ fontWeight: "bold" }}>
              {media.title}
            </Typography>
          </Grid>

          <Grid item>
            {media.thumbnailUrl ? (
              <img
                className={classes.thumbnail}
                src={media.thumbnailUrl}
                alt={`Thumbnail for ${media.title}`}
              />
            ) : (
              <FontAwesomeIcon icon={faWaveformLines} />
            )}
          </Grid>

          <Grid item container justify="center" alignItems="center" spacing={3}>
            {anyPass([isAlbum, isAudiobook])(media) && (
              <Grid item>
                <IconButton onClick={handlePrevTrack}>
                  <FontAwesomeIcon
                    icon={faBackwardStep}
                    className={classes.icon}
                  />
                </IconButton>
              </Grid>
            )}

            <Grid item>
              <IconButton onClick={onClickSkipBack}>
                <FontAwesomeIcon
                  icon={faArrowRotateLeft}
                  className={classes.icon}
                />
              </IconButton>
            </Grid>

            <Grid item>
              <IconButton onClick={handlePlaybackClick}>
                <FontAwesomeIcon
                  icon={playing ? faPause : faPlay}
                  className={classes.icon}
                />
              </IconButton>
            </Grid>

            <Grid item>
              <IconButton onClick={onClickSkipForward}>
                <FontAwesomeIcon
                  icon={faArrowRotateRight}
                  className={classes.icon}
                />
              </IconButton>
            </Grid>

            {anyPass([isAlbum, isAudiobook])(media) && (
              <Grid item>
                <IconButton onClick={handleNextTrack}>
                  <FontAwesomeIcon
                    icon={faForwardStep}
                    className={classes.icon}
                  />
                </IconButton>
              </Grid>
            )}
          </Grid>

          {anyPass([isAlbum, isAudiobook])(media) && notBlank(currentTrack) && (
            <Grid item>
              <Typography style={{ fontStyle: "italic" }}>
                {notBlank(currentTrack.title)
                  ? currentTrack.title
                  : `Chapter ${currentTrack.sequence}`}
              </Typography>
            </Grid>
          )}

          <Grid item style={{ minHeight: 40 }}>
            {any(identity, [
              contentFiles.isLoading,
              compoundChildren.isLoading,
            ]) && <Skeleton height={40} width={630} />}

            {notBlank(tracks) && (
              <ReactJWPlayer
                playerId="player"
                isAutoPlay={NODE_ENV === "production"}
                onAutoStart={handleAutoStart}
                onReady={handlePlayerReady}
                onPlay={handleOnPlay}
                onResume={handleOnResume}
                onPause={handleOnPause}
                onError={handleOnError}
                onPlayAttemptFailed={handleOnPlayAttemptFailed}
                onSetupError={handleOnPlayerSetupError}
                onAutoStartNotAllowed={handleOnAutoStartNotAllowed}
                playlist={tracks}
                className={classes.player}
                playerScript="https://cdn.jwplayer.com/libraries/nSGiuJOY.js"
                customProps={{
                  skin: "five",
                  image: media.thumbnailUrl,
                  height: 40,
                  width: 600,
                }}
              />
            )}
          </Grid>

          {error && (
            <Grid item>
              <Typography style={{ color: "red" }}>{error}</Typography>
            </Grid>
          )}

          <Grid item container justify="space-around">
            <Grid
              item
              xs
              container
              direction="column"
              alignItems="center"
              spacing={3}
            >
              {anyPass([isAlbum, isAudiobook])(media) && (
                <Fragment>
                  <Grid item>
                    <IconButton
                      className={classes.buttonIcon}
                      onClick={handleOpenPlaylist}
                    >
                      <FontAwesomeIcon icon={faBars} className={classes.icon} />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <Typography>
                      {isAudiobook(media) ? "Chapters" : "Tracks"}
                    </Typography>
                  </Grid>
                </Fragment>
              )}
            </Grid>

            <Grid
              item
              xs
              container
              direction="column"
              alignItems="center"
              spacing={3}
            >
              <Grid item>
                <IconButton
                  className={classes.buttonIcon}
                  onClick={onClickChangePlaybackSpeed}
                >
                  <Typography className={classes.playbackRate}>
                    {playbackRate}
                  </Typography>
                </IconButton>
              </Grid>

              <Grid item>
                <Typography>Playback Speed</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      {showPlaylist && (
        <Slide direction="up" in={showPlaylist} mountOnEnter unmountOnExit>
          <Paper square className={classes.playlist}>
            <Grid
              item
              container
              justify="space-between"
              alignItems="center"
              className={classes.playlistHeader}
            >
              <Grid item xs />

              <Grid item xs>
                <Typography>Chapters</Typography>
              </Grid>

              <Grid item>
                <IconButton onClick={handleClosePlaylist}>
                  <FontAwesomeIcon icon={faCircleX} className={classes.icon} />
                </IconButton>
              </Grid>
            </Grid>

            <div className={classes.playlistContainer}>
              {map(
                track => (
                  <Grid
                    key={track.id}
                    onClick={() => handleTrackChange(track)}
                    className={clsx(classes.track, {
                      [classes.trackSelected]: equals(
                        track.id,
                        currentTrack.id,
                      ),
                    })}
                    container
                    item
                    justify="space-between"
                  >
                    <Grid item className={classes.trackSequence}>
                      {track.sequence}
                    </Grid>

                    <Grid item xs>
                      {notBlank(track.title)
                        ? track.title
                        : `Chapter: ${track.sequence}`}
                    </Grid>

                    <Grid item>{formatDuration(track.duration)}</Grid>
                  </Grid>
                ),
                tracks,
              )}
            </div>
          </Paper>
        </Slide>
      )}
    </Dialog>
  );
};

export default AudioViewerDialog;
